'use client'

import type { Sku, SkuPriceOptionsResponse } from '@/types/interfaces/sku'
import { ChatVisitOption } from '@/components/VisitOptions/ChatVisitOption'
import { VideoVisitOption } from '@/components/VisitOptions/VideoVisitOption'
import { EconsultOption } from '@/components/VisitOptions/EconsultOption'
import { OfficeVisitOption } from '@/components/VisitOptions/OfficeVisitOption'
import type { VisitType } from '@/components/VisitOptions/visitOptionsHelpers'

export interface VisitAvailabilityData {
    video?: string
    chat?: string
    econsult?: string
}

interface VisitOptionByTypeProps {
    type: VisitType
    sku: Sku
    prices: SkuPriceOptionsResponse | null
    userHomeTimezone: string | null
    availabilityData: VisitAvailabilityData
    availabilityLoading: boolean
    theme: 'default' | 'tight'
    /** When true and theme is `default`, the office card shows its subtitle. */
    showInPersonSubtitle?: boolean
    /**
     * When true and `type === 'in-person-visit'`, the office card uses
     * responsive sizing (full-width on mobile, 250 on desktop) instead of
     * the fixed 250×250 default. Has no effect on the other card types
     * today.
     */
    responsive?: boolean
    onClick: () => void
    onOfficeVisitClick: (e: React.MouseEvent) => void
}

/** Dispatches to the appropriate visit-option card based on `type`. */
export function VisitOptionByType({
    type,
    sku,
    prices,
    userHomeTimezone,
    availabilityData,
    availabilityLoading,
    theme,
    showInPersonSubtitle = false,
    responsive = false,
    onClick,
    onOfficeVisitClick,
}: VisitOptionByTypeProps) {
    if (type === 'video') {
        return (
            <VideoVisitOption
                sku={sku}
                prices={prices}
                availability={availabilityData.video}
                availabilityLoading={availabilityLoading}
                theme={theme}
                onClick={onClick}
            />
        )
    }
    if (type === 'econsult') {
        return (
            <EconsultOption
                sku={sku}
                prices={prices}
                availability={availabilityData.econsult}
                availabilityLoading={availabilityLoading}
                theme={theme}
                onClick={onClick}
            />
        )
    }
    if (type === 'in-person-visit') {
        return (
            <div onClick={onOfficeVisitClick} style={{ cursor: 'pointer' }}>
                <OfficeVisitOption
                    sku={sku}
                    prices={prices}
                    theme={theme}
                    subtitle={showInPersonSubtitle && theme === 'default' ? 'With a clinician near you' : undefined}
                    responsive={responsive}
                />
            </div>
        )
    }
    return (
        <ChatVisitOption
            sku={sku}
            prices={prices}
            userHomeTimezone={userHomeTimezone}
            availability={availabilityData.chat}
            availabilityLoading={availabilityLoading}
            theme={theme}
            onClick={onClick}
        />
    )
}
